<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { fetchData } from '../../fetchData';
import NoItemFound from '../dashboard/noItemFound.vue';
import ScrollMoreButton from '../ScrollMoreButton.vue';
import SkeletonLoader from '../SkeletonLoader.vue';
import Dropdown from './../Dropdown.vue';
import Sheet from './../Sheet.vue';
import PriceTargetGridItem from './PriceTargetGridItem.vue';
import PriceTargetListItem from './PriceTargetListItem.vue';

const priceTargets = ref([]);
const priceTargetsFilter = ref({});
const priceTargetsMarketsFilter = ref([]);
const priceTargetsMarketsSymbolFilter = ref([]);
const priceTargetsMarketsFilterQuery = ref('');
const searchPhrase = ref('');
const priceTargetsFilterQuery = ref('');
const lastPage = ref(1);
const page = ref(1);
const observer = ref(null);
const sentinel = ref(null);
const appendToList = ref(false);
const firstLoading = ref(true);
const direction = ref('');
const viewMode = ref('list'); // 'list' or 'grid'
const directionValues = ['Long', 'Short'];
const getPriceTargetsFilter = async () => {
    try {
        const { result = [] } = await fetchData('/api/v1/price-targets/filters');

        priceTargetsFilter.value = result.status_map ?? {};

        priceTargetsMarketsFilter.value = result.subscribed_abo_product_symbols_map ?? [];
    } catch (error) {
        console.error('Error fetching price targets:', error);
    }
};
const getPriceTargets = async () => {
    let url = `/api/v1/price-targets?page=${page.value}&page_size=20&sort[by]=updated_at&sort[order]=desc`;
    try {
        if (searchPhrase.value) {
            url += `&search_phrase=${encodeURIComponent(searchPhrase.value)}`;
        }

        if (direction.value) {
            url += `&direction=${encodeURIComponent(direction.value)}`;
        }

        if (priceTargetsFilterQuery.value) {
            url += `&state[]=${encodeURIComponent(priceTargetsFilterQuery.value)}`;
        }

        if (priceTargetsMarketsSymbolFilter.value.length > 0) {
            priceTargetsMarketsSymbolFilter.value.forEach((symbolId) => {
                url += `&symbol_id[]=${symbolId}`;
            });
        }

        const { result = {} } = await fetchData(`${url}`);

        if (appendToList.value) {
            priceTargets.value = [...priceTargets.value, ...result.data];
        } else {
            priceTargets.value = result.data;
        }

        page.value = result.meta?.current_page || 1;
        lastPage.value = result.meta?.last_page || 1;
    } catch (error) {
        console.error('Error fetching price targets:', error);
    }
};
const createObserver = () => {
    if (observer.value) observer.value.disconnect();

    observer.value = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting && page.value < lastPage.value) {
                loadNextPage();
            }
        });
    });

    if (sentinel.value) {
        observer.value.observe(sentinel.value);
    }
};

const debounce = (func, delay) => {
    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};
const handleSearchInput = debounce((value) => {
    page.value = 1;
    searchPhrase.value = value;
    appendToList.value = false;
    getPriceTargets();
}, 300);

onBeforeUnmount(() => {
    if (observer.value) {
        observer.value.disconnect();
    }
});

const loadNextPage = async () => {
    page.value += 1;
    appendToList.value = true;
    await getPriceTargets();
    await nextTick();
    window.dispatchEvent(new Event('resize'));
};

const isAllSelected = (value) => value === null || value === 'Alle' || value === 'All';

const handleDropdownChange = (value) => {
    priceTargetsFilterQuery.value = isAllSelected(value) ? null : value;
    refetchData();
};

const handleDropdownDirectionChange = (value) => {
    if (isAllSelected(value)) {
        direction.value = '';

        return refetchData();
    }

    direction.value = value;
    refetchData();
};

const handleDropdownMarketChange = (value) => {
    if (isAllSelected(value)) {
        priceTargetsMarketsFilterQuery.value = null;
        priceTargetsMarketsSymbolFilter.value = [];

        return refetchData();
    }

    priceTargetsMarketsFilterQuery.value = value;

    const market = priceTargetsMarketsFilter.value.find((item) => item?.product_id === value);
    priceTargetsMarketsSymbolFilter.value = market?.symbols && typeof market.symbols === 'object' ? Object.keys(market.symbols) : [];

    refetchData();
};

const refetchData = () => {
    appendToList.value = false;
    page.value = 1;
    getPriceTargets();
};
const clearSearch = () => {
    searchPhrase.value = '';

    refetchData();
};

onMounted(async () => {
    await getPriceTargetsFilter();
    await getPriceTargets();
    firstLoading.value = false;
    createObserver();
    await nextTick();
    window.dispatchEvent(new Event('resize'));
});
</script>

<template>
    <Sheet compact>
        <div class="flex items-start gap-4 mb-10">
            <div class="flex justify-between w-full">
                <div class="flex flex-col">
                    <p class="font-bold text-lg">{{ $t('dashboard_heading_pricetargets') }}</p>
                    <p class="text-sm text-gray-500">{{ $t('dashboard_description_pricetargets') }}</p>
                </div>
                <div class="hidden 2xl:inline-flex rounded-full bg-dashboard-primary p-2 max-h-14">
                    <button
                        class="flex items-center justify-center w-10 h-10 rounded-full transition duration-300"
                        :class="viewMode === 'list' ? 'bg-white shadow border border-blue-200 text-blue-600 ' : 'text-gray-500 hover:text-gray-700 '"
                        aria-label="Listenansicht"
                        title="Listenansicht"
                        @click="viewMode = 'list'"
                    >
                        <i class="fi fi-rr-list flex items-center justify-center"></i>
                    </button>
                    <button
                        class="flex items-center justify-center w-10 h-10 rounded-full transition duration-300"
                        :class="viewMode === 'grid' ? 'bg-white shadow border border-blue-200 text-blue-600' : 'text-gray-500 hover:text-gray-700'"
                        aria-label="Kachelansicht"
                        title="Kachelansicht"
                        @click="viewMode = 'grid'"
                    >
                        <i class="fi fi-rr-apps flex items-center justify-center"></i>
                    </button>
                </div>
            </div>
        </div>

        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 lg:gap-12 mb-4">
            <div class="relative flex-1 lg:max-w-[370px]">
                <input
                    v-model="searchPhrase"
                    type="text"
                    :placeholder="$t('plain_search')"
                    class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary shadow-sm hover:shadow-md focus:outline-none text-xs duration-300"
                    @input="handleSearchInput($event.target.value)"
                />
                <i v-if="!searchPhrase" class="fi fi-rr-search absolute top-1/2 right-3 transform -translate-y-1/2"></i>
                <i v-else class="fi fi-rr-cross-circle absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer hover:text-gray-600" @click="clearSearch"></i>
            </div>

            <div class="flex flex-col lg:flex-row gap-2 flex-auto lg:w-1/2">
                <Dropdown
                    :dropdown-values="directionValues.map((value) => ({ key: value.toLowerCase(), title: value }))"
                    :selected-dropdown-value="direction"
                    filter-label="Long/Short"
                    @update:selected-dropdown-value="handleDropdownDirectionChange"
                />
                <Dropdown
                    v-if="priceTargetsMarketsFilter && priceTargetsMarketsFilter.length > 1"
                    :dropdown-values="priceTargetsMarketsFilter.map((value) => ({ key: value.product_id, title: value?.product_name }))"
                    :selected-dropdown-value="priceTargetsMarketsFilterQuery"
                    :filter-label="$t('all_markets')"
                    @update:selected-dropdown-value="handleDropdownMarketChange"
                />
                <Dropdown
                    v-if="priceTargetsFilter"
                    :dropdown-values="Object.entries(priceTargetsFilter).map(([key, value]) => ({ key, title: $translate(value) }))"
                    :selected-dropdown-value="priceTargetsFilterQuery"
                    :filter-label="$t('plain_all') + ' ' + $t('plain_state')"
                    @update:selected-dropdown-value="handleDropdownChange"
                />
            </div>
        </div>

        <div class="max-h-[650px] overflow-y-auto">
            <div v-if="firstLoading">
                <div v-for="n in 4" :key="n">
                    <SkeletonLoader />
                </div>
            </div>
            <div v-if="priceTargets.length === 0 && !firstLoading">
                <no-item-found :item-name="$t('price_target')" />
            </div>
            <div v-if="priceTargets.length > 0">
                <transition-group
                    tag="div"
                    name="twfade"
                    appear
                    :class="[viewMode === 'grid' ? 'flex flex-wrap flex-1 gap-4' : 'flex flex-wrap flex-1 gap-4 2xl:gap-0 2xl:flex-col', 'transition-all duration-700 ease-in-out']"
                >
                    <div
                        v-for="(priceTarget, index) in priceTargets"
                        :key="priceTarget.id"
                        class="flex-1 rounded-2xl shadow-sm transform-gpu transition-all duration-700 ease-in-out h-full"
                        :class="index % 2 === 0 ? 'bg-dashboard-primary-light' : ''"
                        :style="{ transitionDelay: index * 80 + 'ms' }"
                    >
                        <PriceTargetGridItem
                            :price-target="priceTarget"
                            :price-targets-states="priceTargetsFilter"
                            class="rounded-xl block 2xl:hidden hover:shadow-md duration-300"
                        />

                        <PriceTargetListItem
                            v-if="viewMode === 'list'"
                            :price-target="priceTarget"
                            :price-targets-states="priceTargetsFilter"
                            class="rounded-xl hidden 2xl:block hover:shadow-md duration-300"
                        />
                        <PriceTargetGridItem
                            v-if="viewMode === 'grid'"
                            :price-target="priceTarget"
                            :price-targets-states="priceTargetsFilter"
                            class="rounded-xl hidden 2xl:block hover:shadow-md duration-300"
                        />
                    </div>
                </transition-group>
            </div>
            <scroll-more-button class="mb-4" />
            <div ref="sentinel" class="h-12 mt-4 flex justify-center items-center text-gray-400">
                <span v-if="page < lastPage">{{ $t('loading') }}...</span>
            </div>
        </div>
    </Sheet>
</template>

<style scoped>
.twfade-enter-active,
.twfade-leave-active {
    @apply transition-opacity transition-transform duration-700 ease-in-out transform-gpu;
}
.twfade-enter-from,
.twfade-leave-to {
    @apply opacity-0 translate-y-2 scale-[0.98];
}
.twfade-enter-to,
.twfade-leave-from {
    @apply opacity-100 translate-y-0 scale-100;
}

.twfade-move {
    @apply transition-transform duration-700 ease-in-out transform-gpu;
}
.twfade-leave-active {
    position: absolute;
}
</style>
